import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import {
  getCostingSettings,
  updateCostingSettings,
  type CostingSettings,
} from '@/services/costingService';

const METHOD_OPTIONS = [
  { key: 'STANDARD' as const, title: 'Standard Cost', subtitle: 'Use BOM rates', desc: 'for planning' },
  { key: 'ACTUAL' as const, title: 'Actual Cost', subtitle: 'Track real', desc: 'expenses' },
  { key: 'BOTH' as const, title: 'Both', subtitle: 'Standard for', desc: 'plan, Actual for tracking' },
];

const LABOUR_OPTIONS = [
  { key: 'HOURLY_RATES' as const, title: 'Hourly Rates', subtitle: 'Operator hours', desc: 'log rate' },
  { key: 'PIECE_RATE' as const, title: 'Piece Rate', subtitle: 'Work center rate', desc: 'per output unit' },
  { key: 'BOTH' as const, title: 'Both', subtitle: 'Combine both', desc: 'wage factors' },
];

const OVERHEAD_OPTIONS = [
  { key: 'PERCENTAGE' as const, title: '% of Material', subtitle: 'Simple fixed %', desc: 'on material cost' },
  { key: 'FIXED' as const, title: 'Fixed Rate', subtitle: 'Budget-based', desc: 'allocation' },
  { key: 'MACHINE_HOURLY' as const, title: 'Machine Hour', subtitle: 'Rate-based', desc: 'per machine' },
];

export default function CostingSetupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<CostingSettings>({
    overheadMethod: 'PERCENTAGE',
    overheadPercentage: 15,
    labourBasis: 'HOURLY_RATES',
    includeRejectionCost: true,
    includeReworkCost: true,
    costingMethod: 'BOTH',
  });
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await getCostingSettings();
      if (res && res.success && res.data) {
        setSettings(res.data);
      }
    } catch (err) {
      console.error("Failed to load costing settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async () => {
    try {
      const res = await updateCostingSettings(settings);
      if (res && res.success) {
        showToast('Costing settings saved to PostgreSQL');
      } else {
        showToast('Failed to save settings');
      }
    } catch (err) {
      console.error("Error saving costing settings:", err);
      showToast('Error saving settings');
    }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/manufacturing/costing')}
              className="p-2 text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer shadow-sm transition-colors"
              title="Back to Dashboard"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Costing Configuration</h1>
              <p className="text-sm text-gray-500 mt-1">Configure global business rules for production cost calculations</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-save-line" />
            Save Settings
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg">
            {toast}
          </div>
        )}

        {/* Section 1 — Costing Method */}
        <section className="bg-white border border-gray-200 rounded-lg p-5 md:p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Costing Method</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {METHOD_OPTIONS.map((opt) => {
              const active = settings.costingMethod === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setSettings((s) => ({ ...s, costingMethod: opt.key }))}
                  className={`text-left p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    active
                      ? 'border-gray-900 bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className={`text-sm font-semibold ${active ? 'text-gray-900' : 'text-gray-700'}`}>
                    {opt.title}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {opt.subtitle}
                    <br />
                    {opt.desc}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Section 2 — Labour Cost Basis */}
        <section className="bg-white border border-gray-200 rounded-lg p-5 md:p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">How to calculate labour cost?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {LABOUR_OPTIONS.map((opt) => {
              const active = settings.labourBasis === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setSettings((s) => ({ ...s, labourBasis: opt.key }))}
                  className={`text-left p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    active
                      ? 'border-gray-900 bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className={`text-sm font-semibold ${active ? 'text-gray-900' : 'text-gray-700'}`}>
                    {opt.title}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {opt.subtitle}
                    <br />
                    {opt.desc}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Section 3 — Overhead Allocation */}
        <section className="bg-white border border-gray-200 rounded-lg p-5 md:p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Overhead Allocation Method</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {OVERHEAD_OPTIONS.map((opt) => {
              const active = settings.overheadMethod === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setSettings((s) => ({ ...s, overheadMethod: opt.key }))}
                  className={`text-left p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    active
                      ? 'border-gray-900 bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className={`text-sm font-semibold ${active ? 'text-gray-900' : 'text-gray-700'}`}>
                    {opt.title}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {opt.subtitle}
                    <br />
                    {opt.desc}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700">Overhead Rate (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                value={settings.overheadPercentage}
                onChange={(e) => setSettings((s) => ({ ...s, overheadPercentage: Number(e.target.value) }))}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
            <span className="text-xs text-gray-500">
              {settings.overheadPercentage}% will be added to material & labour cost as overhead
            </span>
          </div>
        </section>

        {/* Section 4 — Additional Settings */}
        <section className="bg-white border border-gray-200 rounded-lg p-5 md:p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Additional Settings</h2>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.includeRejectionCost}
              onChange={(e) => setSettings((s) => ({ ...s, includeRejectionCost: e.target.checked }))}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            />
            <div>
              <div className="text-sm text-gray-700 font-medium">Include Rejection Cost in Product Cost</div>
              <div className="text-xs text-gray-500">Cost of rejected units added to good unit cost</div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.includeReworkCost}
              onChange={(e) => setSettings((s) => ({ ...s, includeReworkCost: e.target.checked }))}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            />
            <div>
              <div className="text-sm text-gray-700 font-medium">Include Rework Cost</div>
              <div className="text-xs text-gray-500">Rework labour and material cost included in product cost</div>
            </div>
          </label>
        </section>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-save-line" />
            Save Settings
          </button>
        </div>
      </div>
    </AppLayout>
  );
}